import { format, startOfDay } from 'date-fns'
import * as Yup from 'yup'

import {
  CHARACTER_WHITESPACE,
  EMPTY_LIST,
  EMPTY_STRING,
} from '../../../constants/common'
import { DATE_SHORT_FORMAT } from '../../../constants/date'
import { REG_EXP_WYSIWYG_HTML_START } from '../../../constants/regExp'
import i18n from '../../../i18n'
import { getItemsIds } from '../../../utils/common'
import {
  compareDates,
  compareEmailThreadsArrays,
  compareFileArrays,
  comparePrimitives,
  getUpdatedData,
} from '../../../utils/compareData'
import { formatDateToApi, toUkTimezoneDate } from '../../../utils/date'
import { newLineToBrInHtml, plainTextToHtml } from '../../../utils/html'
import { getUserListItemContentText } from '../../../utils/user'
import { EMAIL_ITEM_ID_KEY } from '../../Email'
import { formValidationSchemaNoChanged } from '../../Form'
import { getFormikFormSelectFieldValidationSchema } from '../../Formik'
import { toRequestSelectItem } from '../../Request'
import { toUserSelectItem } from '../../User'
import { toTaskPrioritySelectItem } from '../TaskPrioritySelect'

import {
  TASK_FORM_CONFIGURATION_FIELD_NAME,
  TASK_FORM_CONFIGURATION_FIELD_OPTION_VALUE,
  TASK_FORM_CREATED_FIELD_NAME,
  TASK_FORM_DEADLINE_FIELD_NAME,
  TASK_FORM_DESCRIPTION_FIELD_MAX_LENGTH,
  TASK_FORM_DESCRIPTION_FIELD_NAME,
  TASK_FORM_EMAILS_THREADS_FIELD_NAME,
  TASK_FORM_FILES_FIELD_NAME,
  TASK_FORM_FINISHED_FIELD_NAME,
  TASK_FORM_ID_FIELD_NAME,
  TASK_FORM_MANAGER_FIELD_NAME,
  TASK_FORM_MARK_AS_DONE_FIELD_NAME,
  TASK_FORM_PHASE_FIELD_NAME,
  TASK_FORM_PRIORITY_FIELD_NAME,
  TASK_FORM_SERVICE_REQUEST_FIELD_NAME,
  TASK_FORM_TITLE_FIELD_MAX_LENGTH,
  TASK_FORM_TITLE_FIELD_NAME,
  TASK_FORM_TYPE_FIELD_NAME,
  TASK_FORM_UPDATED_FIELD_NAME,
  TASK_FORM_VISIBILITY_FIELD_NAME,
  TASK_FORM_YES_NO_FIELD_NAME,
} from './TaskForm.constants'

function getReadOnlyFieldActionByText({ date, timezone, user }) {
  return date
    ? [
        format(toUkTimezoneDate(date, timezone), DATE_SHORT_FORMAT),
        user ? i18n.t('Common.ByKey') : null,
        user
          ? getUserListItemContentText({
              companyPostName: user.company_post_name,
              email: user.email,
              fullname: user.fullname,
              isLandlord: user.is_landlord,
              role: user.role,
            })
          : null,
      ]
        .join(CHARACTER_WHITESPACE)
        .trim()
    : null
}

function getEmailThreadValidationSchema() {
  return Yup.object({
    last_message: Yup.object({
      owner_obj: Yup.object({
        name: Yup.string().ensure(),
      }),
      recipient: Yup.object({
        name: Yup.string().ensure(),
      }),
    }),
  })
}

function getDescriptionInitialValue(notes) {
  return REG_EXP_WYSIWYG_HTML_START.test(notes || EMPTY_STRING)
    ? newLineToBrInHtml(notes)
    : plainTextToHtml(notes)
}

export function isTaskFormChecklistConfiguration(values) {
  return (
    values[TASK_FORM_CONFIGURATION_FIELD_NAME] ===
    TASK_FORM_CONFIGURATION_FIELD_OPTION_VALUE.checklist
  )
}

export function isTaskFormYesNoConfiguration(values) {
  return (
    values[TASK_FORM_CONFIGURATION_FIELD_NAME] ===
    TASK_FORM_CONFIGURATION_FIELD_OPTION_VALUE.yesNo
  )
}

export function getTaskFormValidationSchema(initialValues, isEditing) {
  return Yup.object({
    [TASK_FORM_DEADLINE_FIELD_NAME]: Yup.date().nullable(),
    [TASK_FORM_DESCRIPTION_FIELD_NAME]: Yup.string()
      .max(
        TASK_FORM_DESCRIPTION_FIELD_MAX_LENGTH,
        i18n.t('Error.MaxLengthKey', {
          length: TASK_FORM_DESCRIPTION_FIELD_MAX_LENGTH,
        })
      )
      .ensure(),
    [TASK_FORM_EMAILS_THREADS_FIELD_NAME]: Yup.array().of(
      getEmailThreadValidationSchema()
    ),
    [TASK_FORM_MANAGER_FIELD_NAME]: getFormikFormSelectFieldValidationSchema({
      notSelectedValueErrorText: i18n.t(
        'Error.TaskFormManagerFieldNotSelectedValueFromListKey'
      ),
      required: true,
    }),
    [TASK_FORM_PHASE_FIELD_NAME]: Yup.number().required(() => true),
    [TASK_FORM_SERVICE_REQUEST_FIELD_NAME]:
      getFormikFormSelectFieldValidationSchema({
        notSelectedValueErrorText: i18n.t(
          'Error.TaskFormServiceRequestFieldNotSelectedValueFromListKey'
        ),
        required: true,
      }),
    [TASK_FORM_TITLE_FIELD_NAME]: Yup.string()
      .max(
        TASK_FORM_TITLE_FIELD_MAX_LENGTH,
        i18n.t('Error.MaxLengthKey', {
          limit: TASK_FORM_TITLE_FIELD_MAX_LENGTH,
        })
      )
      .required(() => true),
    [TASK_FORM_TYPE_FIELD_NAME]: Yup.object().required(() => true),
  }).when(EMPTY_LIST, {
    is: () => isEditing,
    then: schema =>
      schema.concat(formValidationSchemaNoChanged({ initialValues })),
  })
}

export function getTaskFormConfigurationFieldOptions() {
  return [
    {
      id: TASK_FORM_CONFIGURATION_FIELD_OPTION_VALUE.checklist,
      label: i18n.t('Task.FormConfigurationFieldChecklistOptionLabelKey'),
    },
    {
      id: TASK_FORM_CONFIGURATION_FIELD_OPTION_VALUE.yesNo,
      label: i18n.t('Task.FormConfigurationFieldYesNoOptionLabelKey'),
    },
  ]
}

export function getTaskFormCreationPayload({ timezone, values }) {
  const payload = {
    [TASK_FORM_CONFIGURATION_FIELD_NAME]:
      values[TASK_FORM_CONFIGURATION_FIELD_NAME],
    [TASK_FORM_MANAGER_FIELD_NAME]: values[TASK_FORM_MANAGER_FIELD_NAME].value,
    [TASK_FORM_PHASE_FIELD_NAME]: values[TASK_FORM_PHASE_FIELD_NAME],
    [TASK_FORM_PRIORITY_FIELD_NAME]:
      values[TASK_FORM_PRIORITY_FIELD_NAME]?.value ?? null,
    [TASK_FORM_TITLE_FIELD_NAME]: values[TASK_FORM_TITLE_FIELD_NAME],
    [TASK_FORM_TYPE_FIELD_NAME]: values[TASK_FORM_TYPE_FIELD_NAME].id,
    [TASK_FORM_VISIBILITY_FIELD_NAME]: values[TASK_FORM_VISIBILITY_FIELD_NAME],
  }

  if (isTaskFormChecklistConfiguration(values)) {
    payload[TASK_FORM_MARK_AS_DONE_FIELD_NAME] =
      values[TASK_FORM_MARK_AS_DONE_FIELD_NAME]
  } else if (values[TASK_FORM_YES_NO_FIELD_NAME]) {
    payload[TASK_FORM_YES_NO_FIELD_NAME] = values[TASK_FORM_YES_NO_FIELD_NAME]
  }

  if (values[TASK_FORM_DEADLINE_FIELD_NAME]) {
    payload[TASK_FORM_DEADLINE_FIELD_NAME] = formatDateToApi(
      values[TASK_FORM_DEADLINE_FIELD_NAME],
      timezone
    )
  }

  if (values[TASK_FORM_DESCRIPTION_FIELD_NAME]) {
    payload[TASK_FORM_DESCRIPTION_FIELD_NAME] =
      values[TASK_FORM_DESCRIPTION_FIELD_NAME]
  }

  if (values[TASK_FORM_FILES_FIELD_NAME].length > 0) {
    payload[TASK_FORM_FILES_FIELD_NAME] = getItemsIds(
      values[TASK_FORM_FILES_FIELD_NAME]
    )
  }

  if (values[TASK_FORM_EMAILS_THREADS_FIELD_NAME].length > 0) {
    payload[TASK_FORM_EMAILS_THREADS_FIELD_NAME] = getItemsIds(
      values[TASK_FORM_EMAILS_THREADS_FIELD_NAME],
      EMAIL_ITEM_ID_KEY
    )
  }

  return payload
}

export function getTaskFormEditPayload({ initialValues, timezone, values }) {
  const updatedDate = getUpdatedData({
    [TASK_FORM_DEADLINE_FIELD_NAME]: compareDates(
      initialValues[TASK_FORM_DEADLINE_FIELD_NAME],
      values[TASK_FORM_DEADLINE_FIELD_NAME],
      timezone
    ),
  })

  const payload = getUpdatedData({
    [TASK_FORM_CONFIGURATION_FIELD_NAME]: comparePrimitives(
      initialValues[TASK_FORM_CONFIGURATION_FIELD_NAME],
      values[TASK_FORM_CONFIGURATION_FIELD_NAME]
    ),
    [TASK_FORM_DESCRIPTION_FIELD_NAME]: comparePrimitives(
      initialValues[TASK_FORM_DESCRIPTION_FIELD_NAME] || null,
      values[TASK_FORM_DESCRIPTION_FIELD_NAME] || null
    ),
    [TASK_FORM_EMAILS_THREADS_FIELD_NAME]: compareEmailThreadsArrays(
      initialValues[TASK_FORM_EMAILS_THREADS_FIELD_NAME],
      values[TASK_FORM_EMAILS_THREADS_FIELD_NAME]
    ),
    [TASK_FORM_FILES_FIELD_NAME]: compareFileArrays(
      initialValues[TASK_FORM_FILES_FIELD_NAME],
      values[TASK_FORM_FILES_FIELD_NAME]
    ),
    [TASK_FORM_MANAGER_FIELD_NAME]: comparePrimitives(
      initialValues[TASK_FORM_MANAGER_FIELD_NAME]?.value,
      values[TASK_FORM_MANAGER_FIELD_NAME].value
    ),
    [TASK_FORM_MARK_AS_DONE_FIELD_NAME]: comparePrimitives(
      initialValues[TASK_FORM_MARK_AS_DONE_FIELD_NAME],
      values[TASK_FORM_MARK_AS_DONE_FIELD_NAME]
    ),
    [TASK_FORM_PHASE_FIELD_NAME]: comparePrimitives(
      initialValues[TASK_FORM_PHASE_FIELD_NAME],
      values[TASK_FORM_PHASE_FIELD_NAME]
    ),
    [TASK_FORM_PRIORITY_FIELD_NAME]: comparePrimitives(
      initialValues[TASK_FORM_PRIORITY_FIELD_NAME]?.value ?? null,
      values[TASK_FORM_PRIORITY_FIELD_NAME]?.value ?? null
    ),
    [TASK_FORM_SERVICE_REQUEST_FIELD_NAME]: comparePrimitives(
      initialValues[TASK_FORM_SERVICE_REQUEST_FIELD_NAME].value,
      values[TASK_FORM_SERVICE_REQUEST_FIELD_NAME].value
    ),
    [TASK_FORM_TITLE_FIELD_NAME]: comparePrimitives(
      initialValues[TASK_FORM_TITLE_FIELD_NAME],
      values[TASK_FORM_TITLE_FIELD_NAME]
    ),
    [TASK_FORM_TYPE_FIELD_NAME]: comparePrimitives(
      initialValues[TASK_FORM_TYPE_FIELD_NAME].id,
      values[TASK_FORM_TYPE_FIELD_NAME].id
    ),
    [TASK_FORM_VISIBILITY_FIELD_NAME]: comparePrimitives(
      initialValues[TASK_FORM_VISIBILITY_FIELD_NAME],
      values[TASK_FORM_VISIBILITY_FIELD_NAME]
    ),
    [TASK_FORM_YES_NO_FIELD_NAME]: comparePrimitives(
      initialValues[TASK_FORM_YES_NO_FIELD_NAME],
      values[TASK_FORM_YES_NO_FIELD_NAME]
    ),
  })

  if (
    TASK_FORM_MARK_AS_DONE_FIELD_NAME in payload &&
    isTaskFormYesNoConfiguration(values)
  ) {
    if (!payload[TASK_FORM_MARK_AS_DONE_FIELD_NAME]) {
      payload[TASK_FORM_YES_NO_FIELD_NAME] = null
    } else {
      delete payload[TASK_FORM_MARK_AS_DONE_FIELD_NAME]
    }
  }

  if (TASK_FORM_CONFIGURATION_FIELD_NAME in payload) {
    if (isTaskFormChecklistConfiguration(payload)) {
      payload[TASK_FORM_MARK_AS_DONE_FIELD_NAME] =
        values[TASK_FORM_MARK_AS_DONE_FIELD_NAME]
      payload[TASK_FORM_YES_NO_FIELD_NAME] = null
    } else {
      payload[TASK_FORM_MARK_AS_DONE_FIELD_NAME] = false
    }
  }

  if (TASK_FORM_DEADLINE_FIELD_NAME in updatedDate) {
    const nextDeadline = updatedDate[TASK_FORM_DEADLINE_FIELD_NAME]
      ? formatDateToApi(values[TASK_FORM_DEADLINE_FIELD_NAME], timezone)
      : updatedDate[TASK_FORM_DEADLINE_FIELD_NAME]

    payload[TASK_FORM_DEADLINE_FIELD_NAME] = nextDeadline
  }

  return payload
}

export function getTaskFormInitialValues(item, timezone) {
  return {
    [TASK_FORM_CONFIGURATION_FIELD_NAME]: item.commit_type,
    [TASK_FORM_CREATED_FIELD_NAME]: getReadOnlyFieldActionByText({
      date: item.created,
      timezone,
      user: item.created_by,
    }),
    [TASK_FORM_DEADLINE_FIELD_NAME]: item.deadline
      ? startOfDay(toUkTimezoneDate(item.deadline, timezone))
      : null,
    [TASK_FORM_DESCRIPTION_FIELD_NAME]: getDescriptionInitialValue(item.notes),
    [TASK_FORM_EMAILS_THREADS_FIELD_NAME]: EMPTY_LIST,
    [TASK_FORM_FILES_FIELD_NAME]: EMPTY_LIST,
    [TASK_FORM_FINISHED_FIELD_NAME]: getReadOnlyFieldActionByText({
      date: item.closed_at,
      timezone,
      user: item.closed_by,
    }),
    [TASK_FORM_ID_FIELD_NAME]: item.id,
    [TASK_FORM_MANAGER_FIELD_NAME]: item.manager
      ? toUserSelectItem(item.manager)
      : null,
    [TASK_FORM_MARK_AS_DONE_FIELD_NAME]: !!(item.done || item.yesno),
    [TASK_FORM_PHASE_FIELD_NAME]: item.phase.id,
    [TASK_FORM_PRIORITY_FIELD_NAME]: item.priority
      ? toTaskPrioritySelectItem(item.priority)
      : null,
    [TASK_FORM_SERVICE_REQUEST_FIELD_NAME]: toRequestSelectItem(item.request),
    [TASK_FORM_TITLE_FIELD_NAME]: item.title,
    [TASK_FORM_TYPE_FIELD_NAME]: item.activity_type,
    [TASK_FORM_UPDATED_FIELD_NAME]: getReadOnlyFieldActionByText({
      date: item.updated,
      timezone,
      user: item.updated_by,
    }),
    [TASK_FORM_VISIBILITY_FIELD_NAME]: item.visible_to_tenant,
    [TASK_FORM_YES_NO_FIELD_NAME]: item.yesno,
  }
}

export function isTaskFormEditing(id) {
  return Number.isFinite(id)
}
